import { sortString, sortByDate } from './sort.util';
import moment from 'moment';
import { HeadCell } from '../components/table/index_table.context';
import { CheckboxFilter } from '../components/table/filters/TableCheckboxFilter';
import { TextFilter } from '../components/table/filters/TableTextFilter';
import { getIn } from 'formik';

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const aOrderBy = getIn(a, orderBy as string);
  const bOrderBy = getIn(b, orderBy as string);
  if (aOrderBy && moment(aOrderBy as any).isValid()) {
    if (bOrderBy && moment(bOrderBy as any).isValid()) {
      return sortByDate(aOrderBy, bOrderBy);
    } else {
      return 1;
    }
  } else if (bOrderBy && moment(bOrderBy as any).isValid()) {
    return -1;
  } else {
    if (
      bOrderBy < aOrderBy ||
      (bOrderBy === undefined && aOrderBy !== undefined)
    ) {
      return 1;
    }
    if (
      bOrderBy > aOrderBy ||
      (bOrderBy !== undefined && aOrderBy === undefined)
    ) {
      return -1;
    }
  }
  return 0;
}

export type Order = 'asc' | 'desc';

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort(
  array: any[],
  comparator: (a: any, b: any) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [any, number]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);

    if (order !== 0) return order;

    return a[1] - b[1];
  });

  return stabilizedThis.map(el => el[0]);
}

export const initCheckboxFilters = (
  data: Array<any>,
  columns: Array<HeadCell>
) => {
  const checkboxFilterColumns = columns.filter(column => column.checkboxFilter);

  const checkboxFilters = Object.fromEntries(
    checkboxFilterColumns.map(column => [
      column.name,
      Object.fromEntries(
        data
          .map(record => {
            const cell = getIn(record, column.checkboxFilter || column.name);
            const result = !Array.isArray(cell)
              ? typeof cell === 'string'
                ? [cell]
                : [cell?.name || 'None']
              : cell.filter(v => v).length > 0
              ? cell.filter(v => v).map(cellData => cellData?.name || cellData)
              : ['None'];
            return result;
          })
          .flat()
          .filter((val, index, self) => self.indexOf(val) === index)
          .sort(sortString)
          .map(key => {
            return [key, true];
          })
      ),
    ])
  ) as CheckboxFilter;

  return checkboxFilters;
};

export const initTextFilters = (columns: Array<HeadCell>) => {
  const textFilterColumns = columns.filter(column => column.textFilter);

  const textFilters = Object.fromEntries(
    textFilterColumns.map(column => [column.textFilter, ''])
  ) as TextFilter;
  return textFilters;
};

export const getCheckboxFilteredData = (
  data: Array<any>,
  checkboxFilters: CheckboxFilter
) => {
  const filterNames = Object.keys(checkboxFilters);

  let filteredData = [...data];

  for (let i = 0; i < filterNames.length; i++) {
    const filterName = filterNames[i];
    const filter = checkboxFilters[filterName];

    filteredData = [
      ...filteredData.filter(record => {
        const cell =
          typeof record[filterName] === 'object'
            ? record[filterName]?.sort || record[filterName]?.[filterName]
            : record[filterName];
        if (Array.isArray(cell)) {
          const names = cell
            .filter(c => c)
            ?.map(cellValue => cellValue?.name || cellValue);
          if (names.length > 0) {
            return names.filter(name => filter[name]).length > 0;
          } else {
            return filter['None'];
          }
          return false;
        } else {
          return typeof cell === 'string' ? filter[cell] : filter[cell?.name];
        }
      }),
    ];
  }

  return filteredData;
};

export const getTextFilteredData = (
  data: Array<any>,
  textFilters: TextFilter
) => {
  const filterNames = Object.keys(textFilters);

  let filteredData = [...data];

  for (let i = 0; i < filterNames.length; i++) {
    const filterName = filterNames[i];
    const filter = textFilters[filterName];

    filteredData = [
      ...filteredData.filter(record => {
        const cell = getIn(record, filterName);

        return (
          (!cell && !filter.toLowerCase().trim()) ||
          cell?.toLowerCase()?.includes(filter.toLowerCase().trim())
        );
      }),
    ];
  }

  return filteredData;
};

export const isAppliedCheckboxFilter = (
  defaultCheckboxFilters: CheckboxFilter,
  checkboxFilters: CheckboxFilter,
  filterName: string
) => {
  if (defaultCheckboxFilters && checkboxFilters && filterName) {
    const defaultFilters = defaultCheckboxFilters[filterName];
    const currentFilters = checkboxFilters[filterName];

    const keys = Object.keys(defaultFilters || {});

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];

      if (defaultFilters[key] !== currentFilters[key]) {
        return true;
      }
    }

    return false;
  }

  return false;
};

export const isAppliedCheckboxFilters = (
  defaultCheckboxFilters: CheckboxFilter,
  checkboxFilters: CheckboxFilter
) => {
  if (defaultCheckboxFilters && checkboxFilters) {
    const filterNames = Object.keys(defaultCheckboxFilters);

    for (let i = 0; i < filterNames.length; i++) {
      const filterName = filterNames[i];

      const isApplied = isAppliedCheckboxFilter(
        defaultCheckboxFilters,
        checkboxFilters,
        filterName
      );

      if (isApplied) {
        return true;
      }
    }
  }

  return false;
};

export const isAppliedTextFilter = (
  textFilters: TextFilter,
  filterName: string
) => {
  if (textFilters && filterName) {
    if (textFilters[filterName]?.trim()) {
      return true;
    }

    return false;
  }

  return false;
};

export const isAppliedTextFilters = (textFilters: TextFilter) => {
  if (textFilters) {
    const filterNames = Object.keys(textFilters);

    for (let i = 0; i < filterNames.length; i++) {
      const filterName = filterNames[i];

      const isApplied = isAppliedTextFilter(textFilters, filterName);

      if (isApplied) {
        return true;
      }
    }

    return false;
  }

  return false;
};

export const isAppliedFilters = (
  defaultCheckboxFilters: CheckboxFilter,
  checkboxFilters: CheckboxFilter,
  textFilters: TextFilter
) => {
  return (
    isAppliedCheckboxFilters(defaultCheckboxFilters, checkboxFilters) ||
    isAppliedTextFilters(textFilters)
  );
};
